import {
	faBuilding,
	faCartShopping,
	faCheck,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalCartDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';

/**
 * Client/location selection page.
 *
 * Two-step flow matching v1.0:
 * 1. User clicks a client → fetches and previews that client's cart
 * 2. User clicks "Choose Location" to confirm the switch
 *
 * The current client is highlighted. Clicking the current client
 * just goes back (no switch needed).
 */
export function ClientSelectorPage(): JSX.Element {
	const { t } = useTranslation();
	const {
		isAuthenticated,
		needsClientSelection,
		availableClients,
		activeClientId,
		selectClient,
		isLoading,
		logout,
		token,
	} = usePortalAuth();
	const currentCart = useCartStore((s) => s.cart);

	const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
	const [previewData, setPreviewData] = useState<PortalCartDto | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [justSelected, setJustSelected] = useState(false);

	// Default the selected client to the current active one and show its cart
	useEffect(() => {
		if (activeClientId != null && selectedClientId == null) {
			setSelectedClientId(activeClientId);
			setPreviewData(currentCart);
		}
	}, [activeClientId, selectedClientId, currentCart]);

	// Fetch cart preview when a client is selected (clicked)
	const handleClientClick = useCallback(
		async (clientId: number) => {
			setSelectedClientId(clientId);
			setError(null);

			// If clicking the current active client, show the current cart from store
			if (clientId === activeClientId) {
				setPreviewData(currentCart);
				return;
			}

			// Fetch the preview for a different client
			if (token == null) return;
			setPreviewLoading(true);
			try {
				const res = await cartService.previewCart(token, clientId);
				setPreviewData(res.data);
			} catch {
				setPreviewData(null);
			} finally {
				setPreviewLoading(false);
			}
		},
		[token, activeClientId, currentCart],
	);

	// Confirm location switch
	const handleConfirm = useCallback(async () => {
		if (selectedClientId == null) return;

		// Same client — just go back
		if (selectedClientId === activeClientId) {
			setJustSelected(true);
			return;
		}

		setConfirming(true);
		setError(null);
		try {
			await selectClient(selectedClientId);
			setJustSelected(true);
		} catch (err: unknown) {
			const message =
				err instanceof Error ? err.message : t('Failed to select client');
			setError(t(message));
			setConfirming(false);
		}
	}, [selectedClientId, activeClientId, selectClient, t]);

	// --- Early returns AFTER all hooks ---

	// Redirect home after a successful confirmation
	if (justSelected && isAuthenticated && !needsClientSelection) {
		return <Navigate to="/" replace />;
	}

	// Not logged in
	if (!isAuthenticated && !needsClientSelection) {
		return <Navigate to="/" replace />;
	}

	const previewLineItems = previewData?.lineItems ?? [];
	const hasPreviewItems = previewLineItems.length > 0;

	return (
		<div className="min-h-dvh bg-brand-page-bg px-4 py-8">
			<div className="w-full max-w-md mx-auto">
				<div className="text-center mb-6">
					<h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
						{t('Select Account')}
					</h1>
					<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
						{t('Choose which account you would like to access.')}
					</p>
				</div>

				{error != null && (
					<div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3">
						{error}
					</div>
				)}

				{/* Client list with inline cart preview under selected client */}
				<div className="space-y-2 mb-4">
					{availableClients.map((client) => {
						const isCurrent = client.id === activeClientId;
						const isSelected = client.id === selectedClientId;

						return (
							<div key={client.id}>
								<button
									type="button"
									onClick={() => void handleClientClick(client.id)}
									disabled={isLoading || confirming}
									className={`w-full flex items-center gap-3 px-4 py-3.5 bg-brand-surface border rounded-xl hover:border-portal-accent hover:shadow-sm transition-all min-h-touch disabled:opacity-50 ${
										isSelected
											? 'border-portal-accent ring-1 ring-portal-accent'
											: isCurrent
												? 'border-portal-accent/50'
												: 'border-brand-border'
									}`}
								>
									<div
										className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
											isCurrent
												? 'bg-portal-accent dark:bg-portal-accent/100'
												: 'bg-portal-accent/20'
										}`}
									>
										<FontAwesomeIcon
											icon={isCurrent ? faCheck : faBuilding}
											className={
												isCurrent ? 'text-white' : 'text-portal-accent'
											}
										/>
									</div>
									<div className="flex-1 text-left">
										<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
											{t(client.name)}
										</div>
										{client.city != null && client.state != null ? (
											<div className="text-xs text-brand-muted">
												{t(client.city)}, {t(client.state)}
											</div>
										) : client.externalId != null ? (
											<div className="text-xs text-brand-muted">
												{t('ID')}: {t(client.externalId)}
											</div>
										) : null}
									</div>
									{isCurrent && (
										<span className="text-xs text-portal-accent font-medium">
											{t('Current')}
										</span>
									)}
								</button>

								{/* Cart preview — rendered directly below the selected client */}
								{isSelected && (
									<div className="mt-1 mb-1 bg-brand-input border border-brand-border rounded-lg p-3">
										{previewLoading ? (
											<div className="py-2 text-center">
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin text-gray-400"
												/>
											</div>
										) : hasPreviewItems ? (
											<>
												<div className="flex items-center gap-1.5 mb-1.5">
													<FontAwesomeIcon
														icon={faCartShopping}
														className="text-gray-400 text-xs"
													/>
													<span className="text-xs font-medium text-brand-muted">
														{t('Cart')}
													</span>
												</div>
												<div className="space-y-0.5 mb-1.5">
													{previewLineItems.map((item) => (
														<div
															key={item.id}
															className="flex justify-between text-xs text-gray-600 dark:text-gray-400"
														>
															<span className="truncate flex-1 mr-2">
																{t(item.description)}{' '}
																<span className="text-gray-400">
																	x{item.quantityOrdered}
																</span>
															</span>
															<span className="shrink-0">
																{formatCurrency(item.subtotal)}
															</span>
														</div>
													))}
												</div>
												<div className="flex justify-between text-xs font-medium text-gray-900 dark:text-gray-100 pt-1.5 border-t border-brand-border">
													<span>{t('Subtotal')}</span>
													<span>{formatCurrency(previewData?.subtotal)}</span>
												</div>
											</>
										) : (
											<p className="text-xs text-brand-muted">
												{t('Cart is empty for this location')}
											</p>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>

				{/* Confirm button */}
				<button
					type="button"
					onClick={() => void handleConfirm()}
					disabled={selectedClientId == null || isLoading || confirming}
					className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-4"
				>
					{confirming ? (
						<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
					) : null}
					{confirming ? t('Switching...') : t('Choose Location')}
				</button>

				<div className="text-center">
					<button
						type="button"
						onClick={logout}
						className="text-sm text-brand-muted hover:text-gray-700 dark:hover:text-gray-200 underline"
					>
						{t('Sign out')}
					</button>
				</div>
			</div>
		</div>
	);
}
